import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { Wallet, RefreshCw, ArrowLeft, Search, Download, AlertCircle } from 'lucide-react';

/**
 * 🔒 WALLET RECON - OWNER ONLY
 * Full wallet credit reconciliation per customer.
 * Balance = live SUM of approved transactions (balance_after is unreliable).
 */

const fmt = (n) => { let v = Math.round((Number(n) || 0) * 100) / 100; if (Object.is(v, -0) || v === 0) v = 0; return `R ${v.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
const fmtDate = (d) => new Date(d).toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });

const AdminWalletRecon = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('balance');
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Fetch ALL rows, bypassing the 1000-row default (same proven pattern as Wallet Management)
  const fetchAllRows = async (table, selectColumns, orderColumn = null) => {
    const PAGE_SIZE = 1000;
    let allData = [];
    let from = 0;
    let hasMore = true;
    while (hasMore) {
      let query = supabase.from(table).select(selectColumns).range(from, from + PAGE_SIZE - 1);
      if (orderColumn) query = query.order(orderColumn);
      const { data, error } = await query;
      if (error) throw error;
      allData = allData.concat(data || []);
      hasMore = data && data.length === PAGE_SIZE;
      from += PAGE_SIZE;
    }
    return allData;
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const all = await fetchAllRows('wallet_transactions', 'user_id, transaction_type, status, amount, created_at');

      const agg = {};
      for (const t of all) {
        const u = t.user_id;
        if (!agg[u]) agg[u] = { user_id: u, credited: 0, debited: 0, balance: 0, last: t.created_at, count: 0 };
        agg[u].count++;
        if (t.created_at > agg[u].last) agg[u].last = t.created_at;
        if (t.status === 'approved') {
          if (t.transaction_type === 'credit') { agg[u].credited += Number(t.amount); agg[u].balance += Number(t.amount); }
          else if (t.transaction_type === 'debit') { agg[u].debited += Number(t.amount); agg[u].balance -= Number(t.amount); }
        }
      }

      const userIds = Object.keys(agg);
      const profiles = {};
      for (let i = 0; i < userIds.length; i += 200) {
        const chunk = userIds.slice(i, i + 200);
        const { data: profs } = await supabase
          .from('user_profiles')
          .select('id, name, email')
          .in('id', chunk);
        (profs || []).forEach(p => { profiles[p.id] = p; });
      }

      const rows = userIds.map(id => ({
        ...agg[id],
        balance: Math.round(agg[id].balance * 100) / 100 + 0,   // kill float residue & -0
        credited: Math.round(agg[id].credited * 100) / 100,
        debited: Math.round(agg[id].debited * 100) / 100,
        name: profiles[id]?.name || '(no name)',
        email: profiles[id]?.email || '(no email)',
      }));
      setCustomers(rows);
    } catch (e) {
      console.error('Wallet recon load error:', e);
      alert('Failed to load wallet recon: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async (cust) => {
    setSelected(cust);
    setLedgerLoading(true);
    setLedger([]);
    try {
      const { data, error } = await supabase
        .from('wallet_transactions')
        .select('created_at, transaction_type, status, amount, reason, description, order_id')
        .eq('user_id', cust.user_id)
        .order('created_at', { ascending: true });
      if (error) throw error;

      let bal = 0;
      const rows = (data || []).map(t => {
        const isC = t.transaction_type === 'credit';
        const isAppr = t.status === 'approved';
        if (isAppr) bal += isC ? Number(t.amount) : -Number(t.amount);
        return { ...t, running: isAppr ? bal : null };
      });
      setLedger(rows);
    } catch (e) {
      console.error('Ledger load error:', e);
      alert('Failed to load ledger: ' + e.message);
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => { loadCustomers(); }, []);

  const filtered = customers
    .filter(c => {
      const q = search.toLowerCase();
      return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'balance') return b.balance - a.balance;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'activity') return new Date(b.last) - new Date(a.last);
      return 0;
    });

  const totalOutstanding = customers.reduce((s, c) => s + (c.balance > 0 ? c.balance : 0), 0);

  const exportLedgerCSV = () => {
    if (!selected) return;
    const header = ['Date', 'Type', 'Status', 'Amount', 'Running Balance', 'Reason', 'Description'];
    const lines = ledger.map(t => [
      fmtDate(t.created_at), t.transaction_type, t.status, t.amount,
      t.running != null ? t.running.toFixed(2) : '', t.reason || '', (t.description || '').replace(/"/g, '""')
    ].map(v => `"${v}"`).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `wallet_recon_${selected.email}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (selected) {
    const closing = ledger.length ? (ledger.filter(r => r.running != null).slice(-1)[0]?.running ?? 0) : 0;
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft size={18} /> Back to all customers
        </button>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{selected.name}</h1>
            <p className="text-gray-600 mt-1">{selected.email}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Current Balance</p>
            <p className={`text-3xl font-bold ${closing < -0.005 ? 'text-red-600' : 'text-green-600'}`}>{fmt(closing)}</p>
          </div>
        </div>
        <div className="flex gap-3 mb-4">
          <button onClick={exportLedgerCSV} className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">
            <Download size={16} /> Export CSV
          </button>
        </div>
        {closing < -0.005 && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertCircle size={16} /> This account has a negative balance — investigate (excess debits).
          </div>
        )}
        {ledgerLoading ? (
          <p className="text-gray-500">Loading ledger…</p>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Credit</th>
                  <th className="text-right px-4 py-2">Debit</th>
                  <th className="text-right px-4 py-2">Balance</th>
                  <th className="text-left px-4 py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((t, i) => {
                  const isC = t.transaction_type === 'credit';
                  const isBackfill = (t.description || '').startsWith('Backfill:');
                  const isRej = t.status !== 'approved';
                  return (
                    <tr key={i} className={`border-t ${isBackfill ? 'bg-amber-50' : isRej ? 'bg-gray-50 text-gray-400' : ''}`}>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                      <td className="px-4 py-2 capitalize">{t.transaction_type}</td>
                      <td className="px-4 py-2">{t.status}</td>
                      <td className="px-4 py-2 text-right">{isC && t.status === 'approved' ? fmt(t.amount) : ''}</td>
                      <td className="px-4 py-2 text-right">{!isC && t.status === 'approved' ? fmt(t.amount) : ''}</td>
                      <td className="px-4 py-2 text-right font-medium">{t.running != null ? fmt(t.running) : ''}</td>
                      <td className="px-4 py-2 text-gray-600">{t.description || t.reason || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {ledger.length === 0 && <p className="p-4 text-gray-500 text-center">No transactions</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Wallet Recon</h1>
          <p className="text-gray-600 mt-1">Full wallet credit reconciliation per customer</p>
        </div>
        <button onClick={loadCustomers} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 my-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Customers with wallet activity</p>
          <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total outstanding credit (liability)</p>
          <p className="text-2xl font-bold text-green-600">{fmt(totalOutstanding)}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Negative-balance accounts</p>
          <p className="text-2xl font-bold text-red-600">{customers.filter(c => c.balance < -0.005).length}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 bg-white border rounded-lg px-3 py-2">
          <Search size={18} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…"
            className="flex-1 outline-none text-sm" />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="balance">Sort: Balance (high→low)</option>
          <option value="name">Sort: Name</option>
          <option value="activity">Sort: Recent activity</option>
        </select>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading wallet data…</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-right px-4 py-2">Credited</th>
                <th className="text-right px-4 py-2">Debited</th>
                <th className="text-right px-4 py-2">Balance</th>
                <th className="text-left px-4 py-2">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.user_id} onClick={() => loadLedger(c)}
                  className="border-t hover:bg-green-50 cursor-pointer">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{c.name}</div>
                    <div className="text-gray-500 text-xs">{c.email}</div>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(c.credited)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(c.debited)}</td>
                  <td className={`px-4 py-2 text-right font-bold ${c.balance < -0.005 ? 'text-red-600' : c.balance > 0 ? 'text-green-600' : 'text-gray-400'}`}>{fmt(c.balance)}</td>
                  <td className="px-4 py-2 text-gray-500">{fmtDate(c.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="p-4 text-gray-500 text-center">No customers match</p>}
        </div>
      )}
    </div>
  );
};

export default AdminWalletRecon;
